import { useSearchParams } from "react-router-dom";

import { dayjs } from "@thalamos/common";
import type { AuditTrailEntryKind } from "@thalamos/haven-common";
import {
  AuditTrailEntryKindSchema,
  UserIdSchema,
} from "@thalamos/haven-common";

/** How far back the viewer looks before anyone narrows it. */
const DEFAULT_WINDOW_DAYS = 30;

const asDate = (value: string | null, fallback: string): string =>
  value !== null && dayjs(value, "YYYY-MM-DD", true).isValid()
    ? value
    : fallback;

/**
 * The search the trail viewer is showing, held in the query string.
 *
 * In the URL rather than in component state, unlike the directory filters: a
 * trail search is the thing support people send each other — "look at what this
 * account did on this day" — and a filter held in state cannot be linked to,
 * shared, or reloaded onto. It also means the browser's Back button walks back
 * through searches, which is how a person actually explores a trail.
 *
 * An unparseable date in the URL falls back to the default window rather than
 * being sent on. The endpoint requires a window and would refuse the request, so
 * a mistyped link would otherwise show an error instead of a trail.
 */
export const useTrailSearch = () => {
  const [params, setParams] = useSearchParams();

  const today = dayjs().format("YYYY-MM-DD");
  const monthAgo = dayjs()
    .subtract(DEFAULT_WINDOW_DAYS, "days")
    .format("YYYY-MM-DD");

  const fromDate = asDate(params.get("fromDate"), monthAgo);
  const toDate = asDate(params.get("toDate"), today);

  // Unrecognised kinds are dropped rather than sent: the set is closed, and a
  // stale link naming a kind that has since been removed should show the trail
  // it can rather than fail.
  const kinds = (params.get("kinds")?.split(",") ?? [])
    .map((kind) => AuditTrailEntryKindSchema.safeParse(kind))
    .flatMap((parsed) => (parsed.success ? [parsed.data] : []));

  const userId = params.get("userId") ?? "";
  // A user id that does not parse, such as one still being typed, narrows
  // nothing: the endpoint would refuse it.
  const parsedUserId = UserIdSchema.safeParse(userId);
  const resourceId = params.get("resourceId") ?? "";
  const offset = Number(params.get("offset") ?? "0");

  /**
   * Writes the search back to the URL.
   *
   * Every change but paging returns to the first page: narrowing while on page
   * four asks for rows four pages into a result set that may now have one, and
   * the viewer would render empty for what looks like no reason.
   */
  const update = (
    changes: Partial<{
      fromDate: string;
      toDate: string;
      kinds: AuditTrailEntryKind[];
      userId: string;
      resourceId: string;
      offset: number;
    }>,
  ) => {
    const next = {
      fromDate,
      toDate,
      kinds,
      userId,
      resourceId,
      offset: "offset" in changes ? offset : 0,
      ...changes,
    };

    setParams({
      fromDate: next.fromDate,
      toDate: next.toDate,
      ...(next.kinds.length > 0 ? { kinds: next.kinds.join(",") } : {}),
      ...(next.userId.length > 0 ? { userId: next.userId } : {}),
      ...(next.resourceId.length > 0 ? { resourceId: next.resourceId } : {}),
      ...(next.offset > 0 ? { offset: String(next.offset) } : {}),
    });
  };

  return {
    fromDate,
    toDate,
    kinds,
    userId,
    /** The user the trail is narrowed to: `userId` once it parses. */
    searchedUserId: parsedUserId.success ? parsedUserId.data : undefined,
    resourceId,
    offset: Number.isFinite(offset) && offset > 0 ? offset : 0,
    update,
  };
};
